<template>
  <div
    v-if="entries.length > 0"
    class="square-badge__container"
  >
    <template
      v-for="(entry, key) in entries"
      :key="key"
    >
      <SquareBadeCl v-bind="entry" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useOfferStore } from '~/stores/offers';
import { getDiscount, getPrice, humanizePriceFromCents } from '@i22team/rocket';
import { SquareBadge as SquareBadeCl } from '@i22team/td-longtail-vue-components';
import type { SquareBadgeComponentProps } from '@i22team/td-longtail-vue-components';

import type { Price } from '#shared/types/goliath/goliath.types';
import { template } from 'lodash-es';

export type SquareBadgeProps = {
  offer?: Offer | ListOffer;
  prices?: Price[];
  text?: string;
  footnoteText?: string;
  footnoteTheme?: SquareBadgeComponentProps['theme'];
  customBadges?: SquareBadgeComponentProps[];
  hideDiscountedBadge?: boolean;
  discountText?: string;
  discountTheme?: SquareBadgeComponentProps['theme'];
  className?: string;
  showOnlyOne?: boolean;
};

const props = withDefaults(defineProps<SquareBadgeProps>(), {
  offer: undefined,
  prices: () => [],
  discountText: 'Aktionspreis',
  text: undefined,
  discountTheme: undefined,
  footnoteText: undefined,
  footnoteTheme: undefined,
  customBadges: undefined,
  className: undefined,
});

const { offerBadges } = storeToRefs(useOfferStore());

const prices = computed(() => {
  const p = props.offer?.prices;
  return p && Array.isArray(p) ? p : [];
});

const defaultPrice = computed(() => getPrice(prices.value, 'DEFAULT', 'ONETIME'));

const wasPrice = computed(() => getPrice(prices.value, 'WAS', 'ONETIME'));

const discount = computed(() => {
  if (!prices.value) return undefined;
  return defaultPrice.value?.amount && wasPrice.value?.amount
    ? getDiscount(defaultPrice.value.amount, wasPrice.value.amount)
    : undefined;
});

const productOfferBadges = computed(() => {
  return offerBadges.value.filter(
    (item) => item.articleNumber === props.offer?.product?.articleNumber
  );
});

const entries = computed(() => {
  const badges: SquareBadgeComponentProps[] = [];

  if (props.customBadges && (props.customBadges?.length || 0) > 0) {
    props.customBadges.forEach((b) => {
      badges.push(b);
    });
  }

  if (badges.length > 0 && props.showOnlyOne) return badges;

  productOfferBadges.value.forEach((entry) => {
    if (!entry.badge) return;
    badges.push({
      text: entry.badge.text,
      footnoteText: entry.badge.footnoteText,
      theme: entry.badge.theme ?? 'teal',
    });
    if (props.showOnlyOne) {
      return badges;
    }
  });

  if (props.text) {
    badges.push({
      text: props.text,
      footnoteText: props.footnoteText,
      theme: props.footnoteTheme || 'teal',
    });
    if (props.showOnlyOne) return badges;
  }
  if (discount.value && !props.hideDiscountedBadge) {
    // use lodash delimiters "<%= defaultPrice %>"
    badges.push({
      text: template(props.discountText)({
        defaultPrice: defaultPrice.value?.amount
          ? humanizePriceFromCents(defaultPrice.value.amount)
          : '',
        wasPrice: wasPrice.value?.amount ? humanizePriceFromCents(wasPrice.value.amount) : '',
        discount: discount.value ? `${discount.value}%` : '',
      }),
      footnoteText: props.footnoteText,
      theme: props.discountTheme || 'light-green',
    });
    if (props.showOnlyOne) return badges;
  }
  return badges;
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.square-badge {
  display: inline-flex;
  align-items: center;
  margin: 0 0.25rem 0.5rem;
  padding: 0.688rem 1rem;

  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
}
</style>
